import { Tabletop } from '../Tabletop';
import { ModelSelector } from './ModelSelector';
import {
  DynamicModel,
  WithDimensionsReceivedProps,
  WithDimensionsSentProps,
  BasicDynamicModelEditor,
  useModelWithAppliedActions,
  Expression,
  ExpressionInput,
} from '@k3/core';
import {
  DefaultModelProps,
  Model,
} from '@k3/core/dist/src/lib/types/model.interface';
import { Stack } from '@mui/system';
import { useMemo } from 'react';
import { MeshStandardMaterial, Vector3 } from 'three';
import { TableSubtraction } from './TableSubtraction';

const TischplatteMaterialName = 'Tischplatte';
const KantendekorMaterialName = 'Kantendekor';

type DynamicTabletopSubModelReferences = {
  subtractionModelKeys: string[];
};

type DynamicCornerTabletopReceivedProps = {
  cornerHeight: number;
};

type DynamicCornerTabletopSentProps = {
  cornerHeight: Expression;
};

type DynamicCornerTabletopProps = WithDimensionsReceivedProps &
  DynamicTabletopSubModelReferences &
  DynamicCornerTabletopReceivedProps &
  DefaultModelProps &
  Pick<Model, 'id'>;

const DynamicTabletop = (props: DynamicCornerTabletopProps) => {
  const modelId = props.id;

  const printAreas = useMemo(() => {
    return defaultMaterials.map((m) => {
      return {
        modelId: modelId,
        materialName: m.name,
      };
    });
  }, [defaultMaterials, modelId]);

  const updatedMaterials = useModelWithAppliedActions(
    modelId,
    defaultMaterials,
    printAreas
  );

  const materials = useMemo(
    () => ({
      top: updatedMaterials[TischplatteMaterialName],
      bottom: updatedMaterials[TischplatteMaterialName],
      sides: updatedMaterials[KantendekorMaterialName],
    }),
    [updatedMaterials]
  );

  const newPositionArray = useMemo(() => {
    const position = new Vector3(...(props.position ?? []));
    position.y += props.height / 2;

    return position.toArray();
  }, [props.position, props.height]);

  const subtractions = useMemo(() => {
    if (
      !props.subtractionModelKeys ||
      props.subtractionModelKeys.length === 0
    ) {
      return undefined;
    }

    return props.subtractionModelKeys.map((modelKey) => {
      return () => <TableSubtraction modelKey={modelKey} />;
    });
  }, [props.subtractionModelKeys]);

  return (
    <group userData={{ modelId: props.id }}>
      <Tabletop
        debug={false}
        type={'rectangle-with-corner'}
        position={newPositionArray}
        rotation={props.rotation}
        scale={props.scale}
        width={props.width}
        height={props.height}
        depth={props.depth}
        cornerHeight={props.cornerHeight}
        materials={materials}
        subtractions={subtractions}
      />
    </group>
  );
};

export const cornerTabletop: DynamicModel<
  DynamicCornerTabletopProps,
  WithDimensionsSentProps &
    DynamicTabletopSubModelReferences &
    DynamicCornerTabletopSentProps
> = {
  type: 'cornerTabletop',
  label: 'Ecktisch Anbauplatte',
  disabledForAR: false,
  component: (props) => {
    return <DynamicTabletop {...props} />;
  },
  propsDialog: (props) => {
    return (
      <Stack direction="column" spacing={4}>
        <BasicDynamicModelEditor {...props} materialsAreEditable={false} />
        <ModelSelector
          label={'Schnittmodelle'}
          multiple
          modelKey={props.dynamicModelProps.subtractionModelKeys}
          onChangeMultiple={props.onChange('subtractionModelKeys')}
        />

        <ExpressionInput
          label="Eckhöhe"
          value={props.dynamicModelProps.cornerHeight.expression}
          onChange={(expression) =>
            props.onChange('cornerHeight')({ expression })
          }
        />
      </Stack>
    );
  },
  defaultProps: {
    width: { expression: '1' },
    height: { expression: '1' },
    depth: { expression: '1' },
    cornerHeight: { expression: '0.2' },
    subtractionModelKeys: [],
  },
  materials: [TischplatteMaterialName, KantendekorMaterialName],
};

const defaultMaterials =
  (cornerTabletop?.materials ?? []).map(
    (m) =>
      new MeshStandardMaterial({
        name: m,
        color: 'white',
      })
  ) ?? [];
